import { ApiService } from './api';
import { ApiResponse } from '../types/auth';

export interface OrdersPerDayRequest {
  from: string; // ISO-8601 format
  to: string;   // ISO-8601 format
}

export interface OrdersPerDayResponse {
  date: string;
  completedOrdersCount: number;
  problematicallyCompletedOrdersCount: number;
  cancelledOrdersCount: number;
}

export interface ClientsPerDayRequest {
  from: string; // ISO-8601 format
  to: string;   // ISO-8601 format
}

export interface ClientsPerDayResponse {
  date: string; // Format: YYYY-MM-DD
  iosClientsCount: number;
  androidClientsCount: number;
}

export interface PricesPerDayRequest {
  from: string; // ISO-8601 format
  to: string;   // ISO-8601 format
}

export interface DailyPriceStatisticsResponse {
  date: string; // Format: YYYY-MM-DD
  totalFoodPrice: number;
  totalDeliveryPrice: number;
  totalPlatformProfit: number;
}

export interface RestaurantPerDayRequest {
  from: string; // ISO-8601 format
  to: string;   // ISO-8601 format
  restaurantId: string;
}

export interface DailyRestaurantStatisticsResponse {
  date: string; // Format: YYYY-MM-DD
  ordersCount: number;
  totalFoodPrice: number;
  restaurantProfit: number;
}

export interface OrderedClientsRequest {
  from: string; // ISO-8601 format
  to: string;   // ISO-8601 format
}

export interface OrderedClientsStatisticsResponse {
  date: string; // Format: YYYY-MM-DD
  allOrderedClientsCount: number; // Total clients who have ordered at least once up to this date
  clientsOrderedTwiceCount: number; // Clients who ordered exactly 2 times up to this date
  clientsOrderedMoreThanTwiceCount: number; // Clients who ordered more than 2 times up to this date
}

export interface DriverOrderStatisticsRequest {
  from: string; // ISO-8601 format
  to: string;   // ISO-8601 format
  driversCount: number;
}

export interface DriverOrderStatisticsResponse {
  driverId: number;
  driverName: string;
  driverPhone: string;
  ordersCount: number;
}

export interface DriverEarningsRequest {
  from: string; // ISO-8601 format
  to: string;   // ISO-8601 format
  driverId: number;
}

export interface DriverEarningsResponse {
  ordersCount: number;
  totalDeliveryPrice: number;
  totalPlatformFee: number;
  totalDriverEarning: number;
}

export class StatisticsService {
  private apiService: ApiService;

  constructor() {
    this.apiService = ApiService.getInstance();
  }

  async getOrdersPerDay(request: OrdersPerDayRequest): Promise<ApiResponse<OrdersPerDayResponse[]>> {
    try {
      const params = new URLSearchParams({
        from: request.from,
        to: request.to,
      });

      const response = await this.apiService.get<OrdersPerDayResponse[]>(
        `/admin/statistics/orders-per-day?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch orders per day statistics',
        success: false
      };
    }
  }

  async getClientsPerDay(request: ClientsPerDayRequest): Promise<ApiResponse<ClientsPerDayResponse[]>> {
    try {
      const params = new URLSearchParams({
        from: request.from,
        to: request.to,
      });

      const response = await this.apiService.get<ClientsPerDayResponse[]>(
        `/admin/statistics/clients-per-day?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch clients per day statistics',
        success: false
      };
    }
  }

  async getPricesPerDay(request: PricesPerDayRequest): Promise<ApiResponse<DailyPriceStatisticsResponse[]>> {
    try {
      const params = new URLSearchParams({
        from: request.from,
        to: request.to,
      });

      const response = await this.apiService.get<DailyPriceStatisticsResponse[]>(
        `/admin/statistics/prices-per-day?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch prices per day statistics',
        success: false
      };
    }
  }

  async getRestaurantPerDay(request: RestaurantPerDayRequest): Promise<ApiResponse<DailyRestaurantStatisticsResponse[]>> {
    try {
      const params = new URLSearchParams({
        from: request.from,
        to: request.to,
        restaurantId: request.restaurantId,
      });

      const response = await this.apiService.get<DailyRestaurantStatisticsResponse[]>(
        `/admin/statistics/restaurant-per-day?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch restaurant per day statistics',
        success: false
      };
    }
  }

  async getOrderedClients(request: OrderedClientsRequest): Promise<ApiResponse<OrderedClientsStatisticsResponse[]>> {
    try {
      const params = new URLSearchParams({
        from: request.from,
        to: request.to,
      });

      const response = await this.apiService.get<OrderedClientsStatisticsResponse[]>(
        `/admin/statistics/ordered-clients?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch ordered clients statistics',
        success: false
      };
    }
  }

  async getDriverOrderStatistics(request: DriverOrderStatisticsRequest): Promise<ApiResponse<DriverOrderStatisticsResponse[]>> {
    try {
      const params = new URLSearchParams({
        from: request.from,
        to: request.to,
        driversCount: request.driversCount.toString(),
      });

      const response = await this.apiService.get<DriverOrderStatisticsResponse[]>(
        `/admin/statistics/drivers-orders?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch driver order statistics',
        success: false
      };
    }
  }

  async getDriverEarnings(request: DriverEarningsRequest): Promise<ApiResponse<DriverEarningsResponse>> {
    try {
      const params = new URLSearchParams({
        from: request.from,
        to: request.to,
        driverId: request.driverId.toString(),
      });

      const response = await this.apiService.get<DriverEarningsResponse>(
        `/admin/statistics/driver-earnings?${params.toString()}`
      );

      return {
        data: response,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch driver earnings statistics',
        success: false
      };
    }
  }
}

export const statisticsService = new StatisticsService();
